import { z } from 'zod';
import { authFetch } from './auth';

/**
 * schedule-v2 API surface: friends, host prefs, invite sends, and short-link
 * resolution. Types are defined LOCALLY and parsed leniently — after any
 * mutation the UI re-GETs the list rather than trusting the mutation's
 * response shape, which decouples this client from the worker's exact replies
 * while both sides are built in parallel.
 */

// reconcile with shared/protocol.ts — v2 wire types below are local to the SPA
// until the backend lands its canonical schemas.

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export class ApiV2Error extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

/** Carrier email-to-SMS gateways — labeled picker options. null = email only,
 * never guess a gateway (standing rule). SMS domains, not MMS. */
export const CARRIERS: Array<{ label: string; gateway: string | null }> = [
  { label: 'Not sure — email only', gateway: null },
  { label: 'Verizon', gateway: 'vtext.com' },
  { label: 'AT&T', gateway: 'txt.att.net' },
  { label: 'T-Mobile', gateway: 'tmomail.net' },
  { label: 'Mint Mobile', gateway: 'tmomail.net' },
  { label: 'Metro by T-Mobile', gateway: 'mymetropcs.com' },
  { label: 'Cricket', gateway: 'sms.cricketwireless.net' },
  { label: 'Google Fi', gateway: 'msg.fi.google.com' },
  { label: 'Boost Mobile', gateway: 'sms.myboostmobile.com' },
  { label: 'US Cellular', gateway: 'email.uscc.net' },
  { label: 'Xfinity Mobile', gateway: 'vtext.com' },
];

export function carrierLabel(gateway: string | null | undefined): string {
  if (!gateway) return 'email only';
  return CARRIERS.find((c) => c.gateway === gateway)?.label ?? gateway;
}

// ---------- friends ----------

export const friendSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  // gateway domain (e.g. vtext.com) or null = email only
  carrierGateway: z.string().nullish(),
  attendMode: z.enum(['vr', 'browser']).catch('browser'),
  favorite: z.boolean().catch(false),
});
export type Friend = z.infer<typeof friendSchema>;

/** A named, reusable selection of friends. memberIds arrives already filtered
 * to live friends by the worker, so the UI can trust it without re-checking. */
export const friendGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  memberIds: z.array(z.string()).catch([]),
});
export type FriendGroup = z.infer<typeof friendGroupSchema>;

export interface FriendDraft {
  name: string;
  email?: string;
  phone?: string;
  carrierGateway?: string | null;
  attendMode: 'vr' | 'browser';
  favorite: boolean;
}

// ---------- prefs ----------

export interface Prefs {
  /** THU-72: the name invites say is hosting. '' = don't name a host. */
  displayName: string;
  /** The host's sign-in address. Read-only here — Settings uses it to suggest
   * a display name, and never sends it back. */
  email?: string;
  defaultRoomCode: string;
  discordWebhook: string | null;
}

const prefsShape = z.object({
  displayName: z.string().catch(''),
  email: z.string().optional(),
  defaultRoomCode: z.string().catch(''),
  discordWebhook: z.string().nullish(),
});

// ---------- invite sends ----------

/** One /send recipient: a saved friend by id, OR inline contact details. */
export interface SendInvite {
  friendId?: string;
  name?: string;
  email?: string;
  phone?: string;
  carrierGateway?: string;
  attendMode?: 'vr' | 'browser';
}

export interface SendResult {
  sent: number;
  /** every channel failed for these — they were NOT delivered (THU-185) */
  failed: number;
  /** the reasons, already phrased for a person, deduped */
  failureReasons: string[];
  skipped: number;
  /** true when any send rode Resend's onboarding fallback sender — those mails
   * reach ONLY the Resend account's own inbox, so friends silently get nothing
   * until thursday-night.com is verified in Resend. */
  fallbackWarning: boolean;
}

/** One of the signed-in host's own nights (THU-239). */
export interface MyNight {
  eventId: string;
  title: string | null;
  movieTitle: string | null;
  roomCode: string | null;
  startsAt: string | null;
  hostToken: string | null;
  past: boolean;
}

const myNightSchema = z.object({
  eventId: z.string(),
  title: z.string().nullish().transform((v) => v ?? null),
  movieTitle: z.string().nullish().transform((v) => v ?? null),
  roomCode: z.string().nullish().transform((v) => v ?? null),
  startsAt: z.string().nullish().transform((v) => v ?? null),
  hostToken: z.string().nullish().transform((v) => v ?? null),
  past: z.boolean().catch(false),
});

// ---------- helpers ----------

async function readError(res: Response): Promise<never> {
  const raw = (await res.json().catch(() => null)) as { message?: string } | null;
  throw new ApiV2Error(raw?.message ?? `Request failed (${res.status}).`, res.status);
}

function pickList(raw: unknown, key: string): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object' && raw !== null) {
    const inner = (raw as Record<string, unknown>)[key];
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

export const apiV2 = {
  // ----- the host's own nights (bearer-authed) -----
  /**
   * THU-239. Throws on failure rather than returning [] — an empty list has to
   * mean "you have no nights", never "the read failed"; the worker answers 503
   * STORE_UNAVAILABLE for the second one and the caller shows it.
   */
  async listMyNights(): Promise<MyNight[]> {
    const res = await authFetch('/api/nights');
    if (!res.ok) await readError(res);
    const raw: unknown = await res.json().catch(() => null);
    return pickList(raw, 'nights')
      .map((n) => myNightSchema.safeParse(n))
      .filter((r): r is { success: true; data: MyNight } => r.success)
      .map((r) => r.data);
  },

  // ----- friends CRUD (bearer-authed) -----
  async listFriends(): Promise<Friend[]> {
    const res = await authFetch('/api/friends');
    if (!res.ok) await readError(res);
    const raw: unknown = await res.json().catch(() => null);
    return pickList(raw, 'friends')
      .map((f) => friendSchema.safeParse(f))
      .filter((r): r is { success: true; data: Friend } => r.success)
      .map((r) => r.data);
  },

  async addFriend(draft: FriendDraft): Promise<void> {
    // the worker's friendInputSchema wants absent fields OMITTED (not null/empty)
    const body = {
      name: draft.name,
      ...(draft.email ? { email: draft.email } : {}),
      ...(draft.phone ? { phone: draft.phone } : {}),
      ...(draft.carrierGateway ? { carrierGateway: draft.carrierGateway } : {}),
      attendMode: draft.attendMode,
      favorite: draft.favorite,
    };
    const res = await authFetch('/api/friends', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!res.ok) await readError(res);
  },

  async updateFriend(id: string, draft: FriendDraft): Promise<void> {
    // update semantics: null CLEARS a field, so empty form fields map to null
    const body = {
      id,
      name: draft.name,
      email: draft.email || null,
      phone: draft.phone || null,
      carrierGateway: draft.carrierGateway || null,
      attendMode: draft.attendMode,
      favorite: draft.favorite,
    };
    const res = await authFetch('/api/friends/update', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!res.ok) await readError(res);
  },

  /** Bind a headset's user_code to this signed-in account (device pairing). */
  async claimDeviceCode(userCode: string): Promise<void> {
    const res = await authFetch('/api/device/claim', {
      method: 'POST',
      body: JSON.stringify({ userCode }),
    });
    if (!res.ok) await readError(res);
  },

  // ----- friend groups (bearer-authed) -----
  async listGroups(): Promise<FriendGroup[]> {
    const res = await authFetch('/api/groups');
    if (!res.ok) await readError(res);
    const raw: unknown = await res.json().catch(() => null);
    return pickList(raw, 'groups')
      .map((g) => friendGroupSchema.safeParse(g))
      .filter((r): r is { success: true; data: FriendGroup } => r.success)
      .map((r) => r.data);
  },

  async addGroup(name: string, memberIds: string[]): Promise<void> {
    const res = await authFetch('/api/groups', {
      method: 'POST',
      body: JSON.stringify({ name, memberIds }),
    });
    if (!res.ok) await readError(res);
  },

  async updateGroup(id: string, patch: { name?: string; memberIds?: string[] }): Promise<void> {
    const res = await authFetch('/api/groups/update', {
      method: 'POST',
      body: JSON.stringify({ id, ...patch }),
    });
    if (!res.ok) await readError(res);
  },

  async removeGroup(id: string): Promise<void> {
    const res = await authFetch('/api/groups/remove', {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
    if (!res.ok) await readError(res);
  },

  async removeFriend(id: string): Promise<void> {
    const res = await authFetch('/api/friends/remove', {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
    if (!res.ok) await readError(res);
  },

  // ----- host prefs (bearer-authed) -----
  async getPrefs(): Promise<Prefs> {
    const res = await authFetch('/api/prefs');
    if (!res.ok) await readError(res);
    const raw: unknown = await res.json().catch(() => null);
    const source =
      typeof raw === 'object' && raw !== null && 'prefs' in (raw as Record<string, unknown>)
        ? (raw as Record<string, unknown>).prefs
        : raw;
    const parsed = prefsShape.safeParse(source);
    return {
      displayName: parsed.success ? parsed.data.displayName : '',
      email: parsed.success ? parsed.data.email : undefined,
      defaultRoomCode: parsed.success ? parsed.data.defaultRoomCode : '',
      discordWebhook: parsed.success ? (parsed.data.discordWebhook ?? null) : null,
    };
  },

  async setPrefs(prefs: Prefs): Promise<void> {
    // Send only what the server accepts. `email` is the host's sign-in address,
    // arrives on the GET so Settings can suggest a name from it, and must never
    // be echoed back as if it were an editable pref.
    const res = await authFetch('/api/prefs', {
      method: 'POST',
      body: JSON.stringify({
        displayName: prefs.displayName,
        defaultRoomCode: prefs.defaultRoomCode,
        discordWebhook: prefs.discordWebhook,
      }),
    });
    if (!res.ok) await readError(res);
  },

  // ----- invite send pipeline (host-token gated like the other event routes) -----
  async sendInvites(eventId: string, hostToken: string, invites: SendInvite[]): Promise<SendResult> {
    const res = await authFetch(
      `/api/events/${encodeURIComponent(eventId)}/send?token=${encodeURIComponent(hostToken)}`,
      { method: 'POST', body: JSON.stringify({ invites }) },
    );
    if (!res.ok) await readError(res);
    // worker replies {sent: recipient[], skipped: recipient[]} — count them,
    // tolerating a plain-number shape too
    const raw = (await res.json().catch(() => null)) as {
      sent?: number | unknown[];
      failed?: number | unknown[];
      skipped?: number | unknown[];
    } | null;
    // THU-185: the fallback for a MISSING `sent` field used to be
    // `invites.length` — so a reply we could not understand was reported to the
    // host as every invitation delivered. Nothing is the safer default: an
    // absent field is not evidence of a send.
    const count = (v: number | unknown[] | undefined) =>
      Array.isArray(v) ? v.length : typeof v === 'number' ? v : 0;
    // a per-channel warning/fromFallback flag means the mail "sent" but only to
    // the Resend account inbox (domain unverified) — surface it, never swallow it
    const hasFallback = (e: unknown): boolean => {
      if (typeof e !== 'object' || e === null) return false;
      return (['email', 'sms'] as const).some((k) => {
        const c = (e as Record<string, unknown>)[k];
        if (typeof c !== 'object' || c === null) return false;
        const ch = c as { warning?: unknown; fromFallback?: unknown };
        return Boolean(ch.warning) || ch.fromFallback === true;
      });
    };
    const fallbackWarning = Array.isArray(raw?.sent) ? raw.sent.some(hasFallback) : false;
    const reasonOf = (e: unknown): string | null => {
      if (typeof e !== 'object' || e === null) return null;
      const r = (e as { reason?: unknown }).reason;
      return typeof r === 'string' && r.trim() ? r : null;
    };
    const failureReasons = Array.isArray(raw?.failed)
      ? [...new Set(raw.failed.map(reasonOf).filter((x): x is string => x !== null))]
      : [];
    return {
      sent: count(raw?.sent),
      failed: count(raw?.failed),
      failureReasons,
      skipped: count(raw?.skipped),
      fallbackWarning,
    };
  },

  // ----- /i/ short-link resolution (public, no auth — guests stay code-based) -----
  async resolveInvite(code: string): Promise<{ eventId: string; voterCode: string }> {
    const res = await fetch(`${API_BASE}/api/invites/${encodeURIComponent(code)}`);
    if (!res.ok) await readError(res);
    const raw = (await res.json().catch(() => null)) as {
      eventId?: string;
      voterCode?: string;
    } | null;
    if (!raw?.eventId) throw new ApiV2Error("We couldn't find that invite link.", 404);
    return { eventId: raw.eventId, voterCode: raw.voterCode ?? code };
  },
};
